import React, { FunctionComponent, useEffect, useMemo } from 'react'
import { Text } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import isEqual from 'lodash/isEqual'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import {
  removeUnknownLineItem,
  selectLineItems,
  selectUnknownLineItems,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { LineItem, UnknownLineItem } from '../../../models/line-item'
import { SaleLineItem } from '../sale-line-item/LineItem'
import { UnknownLineItem as SaleUnknownLineItem } from '../unknown-line-item/UnknownLineItem'
import { useHotkeysService, useSaleManager } from '../../../ng-compat/hooks'
import { EVENT } from '../../../constants'
import { setExpandedLineItemId } from '../../../state/slices/uiStateSlice'
import { useBasketFulfillmentGroups } from '../useBasketFulfillmentGroups'
import { useSaleHasServiceOrder } from '../useSaleHasServiceOrder'
import { useSaleIsContinuedServiceSale } from '../useSaleIsContinuedServiceSale'

const isLineItem = (
  lineItem: LineItem | UnknownLineItem
): lineItem is LineItem => 'fulfilmentType' in lineItem

export const SaleSummary: FunctionComponent = () => {
  const lineItems = useAppSelector(selectLineItems, isEqual)
  const unknownLineItems = useAppSelector(selectUnknownLineItems, isEqual)
  const dispatch = useAppDispatch()
  const saleManager = useSaleManager()
  const hotkeysService = useHotkeysService()
  const ref = React.useRef<HTMLLIElement>(null)
  const isServiceSale = useSaleHasServiceOrder()
  const isContinuedServiceSale = useSaleIsContinuedServiceSale()

  // For continued service sales, don't show fulfillment banners
  // (service sale was previously saved and is being resumed, so showing "TO BE ORDERED FOR SERVICE" is confusing)
  const shouldShowFulfillmentBanners = !isContinuedServiceSale

  const {
    nonFulfillmentLineItems,
    pickUpLineItems,
    deliveryLineItems,
    fulfilledLineItems,
    shouldUseFulfilledGrouping,
  } = useBasketFulfillmentGroups(
    lineItems,
    unknownLineItems,
    isServiceSale,
    isContinuedServiceSale
  )

  const lastAddedLineItem = useMemo(() => {
    return [...lineItems, ...unknownLineItems].sort(
      (a, b) => b.dateAdded - a.dateAdded
    )[0]
  }, [lineItems, unknownLineItems])

  useEffect(() => {
    if (!lastAddedLineItem?.localId) {
      return
    }

    const config = {
      action: () => {
        dispatch(setExpandedLineItemId(lastAddedLineItem.localId))
      },
      combo: ['alt+l'],
      summary: 'Toggle last line item',
    }
    hotkeysService.add(config)

    return () => {
      hotkeysService.remove(config.combo)
    }
  }, [dispatch, hotkeysService, lastAddedLineItem])

  const scrollIntoView = () => {
    if (ref.current) {
      ref.current.scrollIntoView()
    }
  }

  useEffect(() => {
    saleManager.on(EVENT.SALE_ADD_LINE_ITEM, scrollIntoView)
    saleManager.on(EVENT.SALE_ADD_UNKNOWN_LINE_ITEM, scrollIntoView)

    return () => {
      saleManager.off(EVENT.SALE_ADD_LINE_ITEM, scrollIntoView)
      saleManager.off(EVENT.SALE_ADD_UNKNOWN_LINE_ITEM, scrollIntoView)
    }
  }, [saleManager])

  const onDeleteUnknownLineItem = (item: UnknownLineItem) => {
    dispatch(removeUnknownLineItem(item.localId))
    saleManager.saveSale()
  }

  const saleListChildren: React.ReactNode[] = []

  if (shouldShowFulfillmentBanners && deliveryLineItems.length > 0) {
    saleListChildren.push(
      <li key="delivery-banner">
        <Text type="signpost" className="sale-fulfilment-banner">
          {isServiceSale ? (
            <T
              _str="TO BE ORDERED FOR SERVICE"
              _context="Fulfillment service message banner"
            />
          ) : (
            <T _str="DELIVER LATER" _context="Fulfillment message banner" />
          )}
        </Text>
      </li>
    )
  }

  deliveryLineItems.forEach(lineItem => {
    saleListChildren.push(
      <li
        className="sale-list-item line-item--separator"
        key={lineItem.localId}
      >
        <SaleLineItem lineItem={lineItem} />
      </li>
    )
  })

  if (shouldShowFulfillmentBanners && pickUpLineItems.length > 0) {
    saleListChildren.push(
      <li key="pickup-banner">
        <Text type="signpost" className="sale-fulfilment-banner">
          {isServiceSale ? (
            <T
              _str="TO BE ORDERED FOR SERVICE"
              _context="Fulfillment service message banner"
            />
          ) : (
            <T _str="PICK UP LATER" _context="Fulfillment message banner" />
          )}
        </Text>
      </li>
    )
  }

  pickUpLineItems.forEach(lineItem => {
    saleListChildren.push(
      <li
        className="sale-list-item line-item--separator"
        key={lineItem.localId}
      >
        <SaleLineItem lineItem={lineItem} />
      </li>
    )
  })

  if (
    shouldShowFulfillmentBanners &&
    (deliveryLineItems.length > 0 || pickUpLineItems.length > 0) &&
    nonFulfillmentLineItems.length > 0
  ) {
    saleListChildren.push(
      <li key="non-fulfillment-banner">
        <Text type="signpost" className="sale-fulfilment-banner">
          {isServiceSale ? (
            <T
              _str="AVAILABLE FOR SERVICE"
              _context="Fulfillment service message banner"
            />
          ) : (
            <T _str="TAKE NOW" _context="Fulfillment type" />
          )}
        </Text>
      </li>
    )
  }

  nonFulfillmentLineItems.forEach(lineItem => {
    saleListChildren.push(
      <li
        className="sale-list-item line-item--separator"
        ref={lastAddedLineItem.localId === lineItem.localId ? ref : null}
        key={lineItem.localId}
      >
        {isLineItem(lineItem) ? (
          <SaleLineItem lineItem={lineItem} />
        ) : (
          <SaleUnknownLineItem
            item={lineItem as UnknownLineItem}
            onDelete={onDeleteUnknownLineItem}
          />
        )}
      </li>
    )
  })

  if (shouldUseFulfilledGrouping && fulfilledLineItems.length > 0) {
    saleListChildren.push(
      <li key="fulfilled-banner">
        <Text type="signpost" className="sale-fulfilment-banner">
          <T _str="FULFILLED" _context="Fulfillment message banner" />
        </Text>
      </li>
    )
  }

  if (shouldUseFulfilledGrouping) {
    fulfilledLineItems.forEach(lineItem => {
      saleListChildren.push(
        <li
          className="sale-list-item line-item--separator"
          ref={lastAddedLineItem.localId === lineItem.localId ? ref : null}
          key={lineItem.localId}
        >
          {isLineItem(lineItem) ? (
            <SaleLineItem lineItem={lineItem} tooltipVariant="fulfilled" />
          ) : (
            <SaleUnknownLineItem
              item={lineItem as UnknownLineItem}
              onDelete={onDeleteUnknownLineItem}
            />
          )}
        </li>
      )
    })
  }

  return <ul className="sale-list">{saleListChildren}</ul>
}
